import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import {
  ConfirmPopupComponent,
  ConfirmPopupData,
  IBadgeComponent,
  IButtonDirective,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuTriggerDirective,
  ITooltipDirective,
  ModalService,
  IIconComponent,
} from "@front/m19-ui";
import { AccessLevel, AccountMarketplace, DataWarehouseShare } from "@front/m19-api-client";
import { DataWarehouseEditionModalComponent } from "@m19-board/data-warehouse/data-warehouse-edition-modal/data-warehouse-edition-modal.component";
import { TranslocoService } from "@jsverse/transloco";
import { DataWarehouseService } from "@m19-board/services/data-warehouse.service";
import { AccountSelectionService } from "@front/m19-services";
import { filter, switchMap } from "rxjs";
import { DataWarehouseListSharePrincipalsModalComponent } from "@m19-board/data-warehouse/data-warehouse-list-share-principals-modal/data-warehouse-list-share-principals-modal.component";
import { toSignal } from "@angular/core/rxjs-interop";
import { map } from "rxjs/operators";
import { DataWarehouseListAccountMarketplaceModalComponent } from "@m19-board/data-warehouse/data-warehouse-list-account-marketplace-modal/data-warehouse-list-account-marketplace-modal.component";
import { OrganizationAccountGroups } from "@front/m19-models";
import { DateTime } from "luxon";

@Component({
  selector: "tr[app-data-warehouse-list-item]",
  imports: [
    IDropdownMenuComponent,
    IDropdownMenuItemDirective,
    IDropdownMenuTriggerDirective,
    IBadgeComponent,
    IButtonDirective,
    ITooltipDirective,
    IIconComponent,
  ],
  templateUrl: "./data-warehouse-list-item.component.html",
  host: {
    class: "group items-center border-y text-sm tracking-tight transition-colors",
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataWarehouseListItemComponent {
  protected readonly modalService = inject(ModalService);
  protected readonly translocoService = inject(TranslocoService);
  private readonly dataWarehouseService = inject(DataWarehouseService);
  private readonly accountSelectionService = inject(AccountSelectionService);

  private readonly BIGQUERY_URL_TEMPLATE = "https://console.cloud.google.com/bigquery/analytics-hub/exchanges";
  private readonly PROJECTS = "projects";
  private readonly PROJECT_ID = 110977875471;
  private readonly LOCATIONS = "locations";
  private readonly DATA_EXCHANGES = "dataExchanges";
  private readonly DATA_WAREHOUSE = "data_warehouse";
  private readonly LISTINGS = "listings";

  protected readonly bigQueryLabel = this.translocoService.translate("data-warehouse.open_in_bigquery");

  readonly dataShare = input.required<DataWarehouseShare>();

  protected readonly editLabel = computed(() => this.translocoService.translate("data-warehouse-edition.edit"));
  protected readonly pauseResumeLabel = computed(() =>
    this.translocoService.translate(this.paused() ? "data-warehouse-pause.resume" : "data-warehouse-pause.pause"),
  );

  protected readonly linkUrl = computed<string>(() => {
    const gcLocation: string = this.dataShare().gcLocation.toString().toLowerCase();
    return `${this.BIGQUERY_URL_TEMPLATE}/${this.PROJECTS}/${this.PROJECT_ID}/${this.LOCATIONS}/${gcLocation.replace("_", "-")}/${this.DATA_EXCHANGES}/${this.DATA_WAREHOUSE}/${this.LISTINGS}/${this.dataShare().organizationId}_${this.dataShare().shareId}`;
  });

  protected readonly paused = computed(() => {
    return this.dataShare().deleted;
  });

  protected readonly displaySharePrincipals = computed<string>(() => {
    const length = this.dataShare().gcPrincipals.length;
    let label = this.dataShare().gcPrincipals[0];
    if (length > 1) {
      label += ` (+${length - 1})`;
    }
    return label;
  });

  protected readonly lastPushedTime = computed<string>(() => {
    if (this.dataShare().lastSuccess) {
      return DateTime.fromFormat(this.dataShare().lastSuccess!, "yyyy-MM-dd HH:mm:ss")
        .toLocal()
        .toFormat("yyyy-MM-dd HH:mm:ssZZ")!;
    }
    return "N/A";
  });

  protected readonly bigQueryButtonDisabled = computed<boolean>(() => this.dataShare().lastSuccess === null);
  protected readonly bigQueryButtonTooltip = computed(() => {
    if (this.bigQueryButtonDisabled()) {
      return this.translocoService.translate("data-warehouse.big_query_button_tooltip_disabled");
    }
    return "";
  });

  protected readonly shouldDisplayDropdownItems = computed<boolean>(() => {
    return (
      this.selectedOrganization() !== undefined &&
      (this.selectedOrganization()!.organization.accessLevel === AccessLevel.ADMIN ||
        this.selectedOrganization()!.organization.accessLevel === AccessLevel.WRITE)
    );
  });

  protected readonly selectedOrganization = toSignal<OrganizationAccountGroups | undefined>(
    this.accountSelectionService.selectedOrganizationAccountGroups$,
  );
  protected readonly accountGroups = toSignal(
    this.accountSelectionService.selectedOrganizationAccountGroups$.pipe(map((org) => org.accountGroups)),
    { requireSync: true },
  );
  protected readonly availableAccountMarketplaces = computed(() => {
    return this.accountGroups()
      .map((accountGroup) => accountGroup.getAccountMarketplaces())
      .flat();
  });

  public openNewTabOnHost(): void {
    const tab: Window | null = window.open(this.linkUrl(), "_blank");
    tab?.focus();
  }

  protected updateShareModal() {
    this.modalService.openModal<DataWarehouseShare, void>(DataWarehouseEditionModalComponent, {
      modalTitle: this.translocoService.translate("data-warehouse-edition.title_update"),
      data: this.dataShare(),
    });
  }

  protected pauseOrResumeShareModal() {
    this.modalService
      .openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
        modalTitle: this.translocoService.translate(
          this.dataShare().deleted ? "data-warehouse-pause.resume_title" : "data-warehouse-pause.pause_title",
        ),
        closeMode: "none",
        data: {
          message: this.translocoService.translate(
            this.dataShare().deleted
              ? "data-warehouse-pause.resume_confirmation"
              : "data-warehouse-pause.pause_confirmation",
            [this.dataShare().shareName],
          ),
        },
      })
      .afterClosed()
      .pipe(
        filter((confirmed) => confirmed),
        switchMap(() =>
          this.dataWarehouseService.updateDataWarehouseShare(
            this.dataShare().shareId!,
            this.dataShare().gcPrincipals,
            this.dataShare().resources,
            !this.dataShare().deleted,
          ),
        ),
      )
      .subscribe();
  }

  protected openShareResourcesModal() {
    this.modalService.openModal<AccountMarketplace[], void>(DataWarehouseListAccountMarketplaceModalComponent, {
      size: "md",
      modalTitle: this.translocoService.translate("data-warehouse-resources.title"),
      data: this.dataShare().resources.map((resource) => {
        const foundAccountMarketplace = this.availableAccountMarketplaces().find(
          (accountMarketplace) => accountMarketplace.accountId === resource.accountId,
        );
        return {
          accountId: resource.accountId!,
          marketplace: resource.marketplace!,
          accountName: foundAccountMarketplace?.accountName ?? "",
          resourceOrganizationId: this.dataShare().organizationId,
        };
      }),
    });
  }

  protected openSharePrincipalsModal() {
    this.modalService.openModal<Array<string>, void>(DataWarehouseListSharePrincipalsModalComponent, {
      size: "sm",
      modalTitle: this.translocoService.translate("data-warehouse-principals.title"),
      data: this.dataShare().gcPrincipals,
    });
  }
}
